/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useState, useCallback, useMemo } from "react";
import { List, ListItem, ListItemText, ListSubheader, Button, TextField } from "@material-ui/core";
import { ConfirmDialog } from "../../../components/ConfirmDialog/ConfirmDialog";
import { v2, errorToObject } from "@prftesp/cle-logger";
import { v4 as uuidv4 } from "uuid";
import { useEffect } from "react";
import { ENDPOINTS } from "../../../utility/Endpoints";

export const ConfigurationsList = ({
	configurations,
	addConfigurationHandler,
	selectConfigurationHandler,
	selectedConfiguration,
	selectedConfigId,
	formEdited,
}) => {
	const [searchTerm, setSearchTerm] = useState("");
	const [editConfirmOpen, setEditConfirmOpen] = React.useState(false);
	const [Configuration, setConfiguration] = useState(null);

	const handleSearchChange = useCallback((e) => {
		setSearchTerm(e.target.value);
	}, []);

	const addHandler = async () => {
		const newConfig = {
			callMeNowConfigId: uuidv4(),
			config: {
				configName: "Untitled Configuration",
				instanceId: process.env.REACT_APP_CONNECT_INSTANCE_ID,
				startCallAPI: `${ENDPOINTS.ADMIN_API}/startCallMeNow`,
				defaultBucketLink: ENDPOINTS.CALL_ME_NOW_CF,
				subjects: [],
				enabledCallOptions: false,
				allowedOrigins: "",
			},
		};
		await addConfigurationHandler(newConfig, "Successfully created configuration.", "Failed to create configuration.");
	};

	const searchMatches = (
		configurations?.filter((config) => {
			return (
				config.config?.configName?.toLowerCase()?.includes(searchTerm?.toLowerCase() || "") ||
				config.callMeNowConfigId.toLowerCase().includes(searchTerm.toLowerCase())
			);
		}) || []
	).sort((a, b) => (a.config?.configName?.toLowerCase() > b.config?.configName?.toLowerCase() ? 1 : -1));

	useEffect(() => {
		if (!selectedConfiguration && searchMatches && searchMatches.length > 0) {
			selectConfigurationHandler(searchMatches[0]);
		}
	}, [searchMatches, selectConfigurationHandler, selectedConfiguration]);

	const sortedConfigurations = useMemo(() => {
		return [...(configurations || [])].sort?.((a, b) =>
			String(a.config?.configName).toUpperCase() < String(b.config?.configName).toUpperCase() ? -1 : 1,
		);
	}, [configurations]);

	return (
		<div>
			<List
				component="nav"
				subheader={
					<ListSubheader disableSticky component="div">
						<div style={{ marginBottom: 8 }}>
							<TextField
								style={{ width: "100%" }}
								size="small"
								label="Search configurations"
								value={searchTerm}
								onChange={handleSearchChange}
							/>
						</div>
						<div className="d-flex flex-row justify-content-between align-items-center flex-wrap">
							<span>Configurations</span>

							<Button
								variant="outlined"
								className="mb-2 mb-md-0"
								size="small"
								disabled={!!selectedConfigId && formEdited}
								onClick={addHandler}
							>
								Add
							</Button>
						</div>
					</ListSubheader>
				}
			>
				{configurations?.length && searchMatches.length > 0 ? (
					sortedConfigurations.map?.((x) => {
						const searchMatch = searchMatches.find((s) => s.callMeNowConfigId === x.callMeNowConfigId);
						return (
							<ListItem
								button
								selected={x.callMeNowConfigId === selectedConfigId}
								onClick={() => {
									setConfiguration(x);
									formEdited ? setEditConfirmOpen(true) : selectConfigurationHandler(x);
								}}
								key={x.callMeNowConfigId}
								className={searchMatch ? "d-flex" : "d-none"}
							>
								<ListItemText style={{ overflowWrap: "break-word" }} primary={x?.config?.configName} />
							</ListItem>
						);
					})
				) : (
					<ListItem>
						<span className="text-muted font-italic">
							{!searchTerm
								? "No configurations yet, click the 'Add' button to create a new one."
								: "No configurations found."}
						</span>
					</ListItem>
				)}
			</List>
			<ConfirmDialog
				title={"Switch Configuration?"}
				message={"You have unsaved changes that will be discarded if you proceed. Are you sure?"}
				confirmOpen={editConfirmOpen}
				onClose={async (confirmed) => {
					try {
						if (confirmed) {
							selectConfigurationHandler(Configuration);
						}
						setEditConfirmOpen(false);
					} catch (error) {
						v2.error(`Error while cancelling configuration.`, {
							additionalData: { error: errorToObject(error) },
						});
					}
				}}
			/>
		</div>
	);
};
